import { AUTH } from '../foundation';
import { TUseCache, TWidget } from '@libs/types';
import { selectCurrentPageId, selectScenarioId, useAppSelector } from '../store';
import { useCallback, useEffect } from 'react';

const simpleHash = (str: string | null | undefined): string => {
    if (!str) {
        return 'default';
    }
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash |= 0; // Convert to 32bit integer
    }
    return hash.toString();
};

const CACHE_PREFIX = 'ps3';

let memoizedPrefix: string | null = null;

const getCachePrefix = (): string => {
    if (memoizedPrefix) {
        return memoizedPrefix;
    }

    const authLocalStorage = localStorage?.getItem(AUTH);
    if (authLocalStorage) {
        try {
            // If value is a valid JSON, it's an auth object.
            // If not, it's a deviceId string, and parsing will fail.
            const parsedAuth = JSON.parse(authLocalStorage);
            memoizedPrefix = CACHE_PREFIX + simpleHash(parsedAuth?.token);
            return memoizedPrefix;
        } catch (e) {
            // Suppress: value is not a valid JSON auth object (likely a deviceId)
            console.error(e);
        }
    }
    return CACHE_PREFIX;
};

/**
 * IndexedDB layout: a single database with one object store holding every
 * cached widget payload. Each record carries its own metadata (owner, expiry,
 * schema version), so entries for different pages/scenarios coexist instead of
 * wiping each other out — eviction happens lazily by age / owner / version
 * during {@link sweep}, not aggressively on every mount.
 */
const DB_NAME = 'ps3_cache';
const STORE_NAME = 'entries';
const DB_VERSION = 1;

/**
 * Bump to invalidate every previously cached payload after the data shape
 * changes (e.g. a deploy that renames fields). Records with a different
 * `version` are treated as a miss and swept.
 */
const SCHEMA_VERSION = 1;

const DEFAULT_TTL_MINUTES = 30;

/** Upper bound on stored entries; the oldest (by `updatedAt`) are evicted first. */
const MAX_ENTRIES = 50;

type CacheRecord<TD> = {
    data: TD;
    /** Epoch ms after which the record is considered stale. */
    expiry: number;
    /** Namespace tag (from {@link getCachePrefix}); records from another owner are never returned. */
    owner: string;
    /** Schema version the payload was written with. */
    version: number;
    /** Epoch ms of the last write; used for LRU eviction. */
    updatedAt: number;
};

const isIdbSupported = (): boolean => typeof indexedDB !== 'undefined';

/**
 * Resets the memoized cache prefix so the next `getCachePrefix()` call
 * recomputes it from the current auth state. Call after clearing caches on
 * logout when the page is not reloaded.
 */
export const resetCachePrefix = (): void => {
    memoizedPrefix = null;
    sweepPromise = null;
};

let dbPromise: Promise<IDBDatabase> | null = null;

const openDB = (): Promise<IDBDatabase> => {
    if (dbPromise) {
        return dbPromise;
    }
    dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME);
            }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
        request.onblocked = () => reject(request.error);
    });
    // Don't leave a rejected promise cached — the next caller should retry.
    dbPromise.catch(() => {
        dbPromise = null;
    });
    return dbPromise;
};

/**
 * Runs `fn` inside a single transaction and resolves once the transaction
 * *commits* (not merely when the request succeeds), so writes are durable.
 */
const runTx = <T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> =>
    openDB().then(
        (db) =>
            new Promise<T | undefined>((resolve, reject) => {
                const transaction = db.transaction(STORE_NAME, mode);
                const store = transaction.objectStore(STORE_NAME);
                let result: T | undefined;
                const request = fn(store);
                if (request) {
                    request.onsuccess = () => {
                        result = request.result;
                    };
                    request.onerror = () => reject(request.error);
                }
                transaction.oncomplete = () => resolve(result);
                transaction.onerror = () => reject(transaction.error);
                transaction.onabort = () => reject(transaction.error);
            }),
    );

let sweepPromise: Promise<void> | null = null;

/**
 * Deletes expired records, records belonging to another `owner`, records from
 * an older schema version, and — once those are gone — the oldest survivors
 * beyond {@link MAX_ENTRIES}. Runs at most once per session (see {@link ensureSwept}).
 */
const sweep = (owner: string): Promise<void> =>
    openDB().then(
        (db) =>
            new Promise<void>((resolve, reject) => {
                const transaction = db.transaction(STORE_NAME, 'readwrite');
                const store = transaction.objectStore(STORE_NAME);
                const now = Date.now();
                const survivors: Array<{ key: IDBValidKey; updatedAt: number }> = [];

                const cursorRequest = store.openCursor();
                cursorRequest.onsuccess = () => {
                    const cursor = cursorRequest.result;
                    if (cursor) {
                        const record = cursor.value as CacheRecord<unknown> | undefined;
                        const isStale = !record || record.version !== SCHEMA_VERSION || record.owner !== owner || now >= record.expiry;
                        if (isStale) {
                            cursor.delete();
                        } else {
                            survivors.push({ key: cursor.key, updatedAt: record.updatedAt ?? 0 });
                        }
                        cursor.continue();
                    } else if (survivors.length > MAX_ENTRIES) {
                        survivors
                            .sort((a, b) => a.updatedAt - b.updatedAt)
                            .slice(0, survivors.length - MAX_ENTRIES)
                            .forEach((entry) => store.delete(entry.key));
                    }
                };
                cursorRequest.onerror = () => reject(cursorRequest.error);

                transaction.oncomplete = () => resolve();
                transaction.onerror = () => reject(transaction.error);
                transaction.onabort = () => reject(transaction.error);
            }),
    );

const ensureSwept = (owner: string): Promise<void> => {
    if (!sweepPromise) {
        sweepPromise = sweep(owner).catch((e) => {
            console.warn('Failed to sweep cache:', e);
        });
    }
    return sweepPromise;
};

/**
 * Drops the entire cache database (every owner's entries), regardless of the
 * auth-token hash. Intended to be called imperatively on logout / closeFramework.
 */
export const clearAllCaches = async (): Promise<void> => {
    resetCachePrefix();

    if (!isIdbSupported()) {
        return;
    }

    // Close any open connection so deleteDatabase isn't blocked.
    if (dbPromise) {
        try {
            (await dbPromise).close();
        } catch (e) {
            console.warn('Failed to close cache db:', e);
        }
        dbPromise = null;
    }

    await new Promise<void>((resolve) => {
        const request = indexedDB.deleteDatabase(DB_NAME);
        request.onsuccess = () => resolve();
        // Resolve (not reject) on error/blocked: logout cleanup is best-effort.
        request.onerror = () => resolve();
        request.onblocked = () => resolve();
    });
};

type TUseCacheOptions = {
    /** Time-to-live in minutes before a cached entry is treated as a miss. */
    ttlMinutes?: number;
};

export const useCache = <TD>(widget: TWidget<TD>, options?: TUseCacheOptions): TUseCache<TD> => {
    const scenarioId = useAppSelector(selectScenarioId);
    const currentPageId = useAppSelector(selectCurrentPageId);

    const widgetKey = widget.internalId || widget.uid;
    const ttlMinutes = options?.ttlMinutes ?? DEFAULT_TTL_MINUTES;

    // Owner is baked into the key so different users on a shared device never collide.
    const recordKey = `${getCachePrefix()}::${scenarioId}::${currentPageId}::${widgetKey}`;

    useEffect(() => {
        if (!isIdbSupported()) {
            return;
        }
        void ensureSwept(getCachePrefix());
    }, []);

    return {
        putCache: useCallback(
            async (data: TD) => {
                if (!isIdbSupported()) {
                    return;
                }
                const now = Date.now();
                const record: CacheRecord<TD> = {
                    data,
                    expiry: now + ttlMinutes * 60 * 1000,
                    owner: getCachePrefix(),
                    version: SCHEMA_VERSION,
                    updatedAt: now,
                };
                await runTx('readwrite', (store) => store.put(record, recordKey));
            },
            [recordKey, ttlMinutes],
        ),
        getCache: useCallback(async () => {
            if (!isIdbSupported()) {
                return null;
            }
            const record = (await runTx<CacheRecord<TD>>('readonly', (store) => store.get(recordKey) as IDBRequest<CacheRecord<TD>>)) ?? null;

            if (!record) {
                return null;
            }

            const isStale = record.version !== SCHEMA_VERSION || record.owner !== getCachePrefix() || Date.now() >= record.expiry;
            if (isStale) {
                await runTx('readwrite', (store) => store.delete(recordKey));
                return null;
            }

            return record.data;
        }, [recordKey]),
        clearCache: useCallback(async () => {
            if (!isIdbSupported()) {
                return;
            }
            await runTx('readwrite', (store) => store.delete(recordKey));
        }, [recordKey]),
    };
};
